import { useCallback, useEffect, useState } from "react";
import { Search, PackageOpen, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { MOVE_TYPES, MOVE_STATUS, fmtUnits, lastDays } from "@/lib/loyalty";
import { cn } from "@/lib/utils";
import { PeriodPicker } from "./PeriodPicker";
import { MultiFilter } from "./MultiFilter";
import { PointsDialog } from "./PointsDialog";
import { toast } from "sonner";

const TYPES = Object.entries(MOVE_TYPES);
const STATUSES = Object.entries(MOVE_STATUS);
const TONE = { pendente: "bg-amber-500/15 text-amber-600 dark:text-amber-400", confirmado: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", cancelado: "bg-red-500/15 text-red-500" };

export const StatusBadge = ({ s }) => <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", TONE[s])}>{MOVE_STATUS[s] || s}</span>;

function Rows({ rows, mode }) {
  return rows.map((m) => (
    <TableRow key={m.id} data-testid={`activity-row-${m.id}`}>
      <TableCell className="font-mono text-xs">{m.ref_label || "—"}{m.note && <span className="block font-sans text-muted-foreground">{m.note}</span>}</TableCell>
      <TableCell className="whitespace-nowrap text-sm">{fmtDateTime(m.created_at)}</TableCell>
      <TableCell className="text-sm">{m.customer_name}<span className="block text-xs text-muted-foreground">{m.customer_phone}</span></TableCell>
      <TableCell className="text-sm">{MOVE_TYPES[m.type] || m.type}</TableCell>
      <TableCell className={cn("whitespace-nowrap text-right font-mono font-semibold", m.units < 0 ? "text-red-500" : "text-emerald-500")}>{m.units > 0 ? "+" : ""}{fmtUnits(mode, m.units)}</TableCell>
      <TableCell><StatusBadge s={m.status} /></TableCell>
      <TableCell className="text-sm">{m.user_name || "—"}</TableCell>
    </TableRow>
  ));
}

export function LoyaltyActivities({ cfg }) {
  const [period, setPeriod] = useState(() => lastDays(7));
  const [types, setTypes] = useState(TYPES.map(([k]) => k));
  const [statuses, setStatuses] = useState(STATUSES.map(([k]) => k));
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [scoring, setScoring] = useState(false);
  const load = useCallback(() => {
    if (!types.length || !statuses.length) return setRows([]);
    return api.get("/loyalty/activities", { params: { ...period, types: types.join(","), statuses: statuses.join(","), q } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [period, types, statuses, q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  return (
    <div className="space-y-4" data-testid="loyalty-activities">
      <div className="flex flex-wrap items-center gap-3">
        <PeriodPicker value={period} onChange={setPeriod} />
        <MultiFilter label="Tipo" options={TYPES} value={types} onChange={setTypes} testId="activity-type-filter" />
        <MultiFilter label="Status" options={STATUSES} value={statuses} onChange={setStatuses} testId="activity-status-filter" />
        <div className="relative min-w-[200px] flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquise por um cliente" className="pl-9" data-testid="activity-search-input" /></div>
        <Button onClick={() => setScoring(true)} className="h-10" data-testid="activity-scorer-btn"><Coins className="mr-2 h-4 w-4" />Pontuador</Button>
      </div>
      <div className="card-surface overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Pedido</TableHead><TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead>Tipo</TableHead><TableHead className="text-right">{cfg.mode === "cashback" ? "Valor" : "Pontos"}</TableHead><TableHead>Status</TableHead><TableHead>Usuário</TableHead></TableRow></TableHeader>
          <TableBody>{rows && <Rows rows={rows} mode={cfg.mode} />}</TableBody>
        </Table>
        {rows && !rows.length && <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground" data-testid="activity-empty"><PackageOpen className="h-12 w-12 opacity-40" /><p>Nada encontrado</p></div>}
      </div>
      <PointsDialog open={scoring} onOpenChange={setScoring} mode={cfg.mode} onDone={load} />
    </div>
  );
}
